import Cookies from 'js-cookie';
import {createRouter, createWebHistory} from 'vue-router';
import store from '@/store';
import {getAuthToken} from '@/utils/auth-token';
import {canAccessRoute, customerHomePath, normalizeAccessContext, normalizeRouteMeta} from '@/utils/access-context';
import {accountRoutes} from '@/views/routes/account-routes';
import {authRoutes} from '@/views/routes/auth-routes';
import {customerRoutes} from '@/views/routes/customer-routes';
import {workspaceLegacyRoutes, workspaceRoutes} from '@/views/routes/workspace-routes';
import PageNotFoundPage from '@/views/pages/error/not-found';
import VaultShareOpenPage from '@/views/pages/vault/share-open';

import 'vue-simple-context-menu/dist/vue-simple-context-menu.css';

const hasResolvedAuthenticatedHome = function (access) {
    const normalizedAccess = normalizeAccessContext(access, store.state.user || null);
    const homePath = customerHomePath(normalizedAccess);

    return !!normalizedAccess.group && homePath !== '/auth/login';
};

const resolveAuthenticatedAccess = function () {
    if (store.state.user && hasResolvedAuthenticatedHome(store.state.access)) {
        return Promise.resolve(normalizeAccessContext(store.state.access, store.state.user));
    }

    return axios.get('api/auth/user').then(function (response) {
        store.commit('updateUser', response.data);

        return normalizeAccessContext(
            response && response.data ? (response.data.access || {}) : {},
            response && response.data ? response.data : null
        );
    });
};

const routes = [
    {
        path: '/',
        redirect: '/auth/login',
    },
    {
        path: '/share/vault/:selector',
        component: VaultShareOpenPage,
    },
    {
        path: '/vault/share/:selector',
        component: VaultShareOpenPage,
    },
    ...authRoutes,
    ...customerRoutes,
    ...workspaceRoutes,
    ...workspaceLegacyRoutes,
    ...accountRoutes,
    {
        path: '/:pathMatch(.*)*',
        component: PageNotFoundPage,
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes,
});

router.beforeResolve((to, from, next) => {
    const normalizedMeta = normalizeRouteMeta(to.meta || {});
    if (!normalizedMeta.middleware.length && !normalizedMeta.required_capability) {
        next();
        return;
    }

    const token = getAuthToken();
    if (!token && normalizedMeta.requires_auth) {
        Cookies.set('intended_url', to.path);
        next('/auth/login');
        return;
    }

    if (!window.app.register && normalizedMeta.requires_registration) {
        next('/auth/login');
        return;
    }

    if (token && normalizedMeta.requires_guest) {
        resolveAuthenticatedAccess().then(function (access) {
            const homePath = customerHomePath(access);

            if (!homePath || homePath === to.path) {
                next();
                return;
            }

            next(homePath);
        }).catch(function () {
            store.commit('logout');
            next();
        });
        return;
    }

    if (!token || !normalizedMeta.requires_auth) {
        next();
        return;
    }

    axios.post('api/auth/check', {
        capability: normalizedMeta.required_capability,
    }).then(function (response) {
        const nextContext = normalizeAccessContext(
            response && response.data ? response.data.context : {},
            store.state.user || null
        );
        store.commit('setAccessContext', nextContext);

        const routeAllowed = canAccessRoute({
            path: to.path,
            meta: normalizedMeta,
            access: nextContext,
            hasToken: true,
        });

        if (!response.data.access || !routeAllowed) {
            next(customerHomePath(nextContext));
            return;
        }

        next();
    }).catch(function () {
        Cookies.set('intended_url', to.path);
        next('/auth/login');
    });
});

export default router;
